import { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, IconButton, CircularProgress, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, InputAdornment, Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import api from '../../config/api';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';
import formatRut from '../../erp/formatRut';

/**
 * Estado del vínculo cliente ↔ centro de costo Talana (externalRefs.talana),
 * mismo criterio que el chip de la ficha (ErpClientTalanaLink).
 */
const TalanaChip = ({ link }) => {
  const ref = link;
  const { t } = useTranslation();
  if (!ref) return <Chip size="small" variant="outlined" label={t('erp.clients.talana.none')} />;
  if (ref.status === 'linked' || ref.status === 'created') {
    return (
      <Tooltip title={`${t('erp.clients.talana.costCenter')}: ${ref.name || ''} · ID ${ref.id}`}>
        <Chip size="small" color={ref.lastError ? 'warning' : 'success'} variant="outlined"
          label={`Talana · ${ref.code || ref.id}`} />
      </Tooltip>
    );
  }
  if (ref.status === 'failed') {
    return (
      <Tooltip title={ref.lastError?.message || ''}>
        <Chip size="small" color="error" variant="outlined" label={t('erp.clients.talana.failed')} />
      </Tooltip>
    );
  }
  return <Chip size="small" variant="outlined" label={t('erp.clients.talana.pending')} />;
};
const talanaChip = (link) => <TalanaChip link={link} />;

const ErpClientsLayout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { organization, authToken, showAlert } = useStore.getState();

  const [clients, setClients] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadClients = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get('/erp/clients', {
        params: { organizationId: organization },
        headers: { Authorization: `Bearer ${authToken}` },
      });

      if (response.data.response?.status_response === 'OK') {
        setClients(response.data.response.data || []);
      } else {
        setError(t('erp.clients.loadError'));
      }
    } catch (err) {
      console.error(err);
      setError(t('erp.clients.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (organization) loadClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      const response = await api.delete(`/erp/clients/${deleteTarget.uid}`, {
        params: { organizationId: organization },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (response.data.response?.status_response === 'OK') {
        showAlert(t('erp.clients.deleteSuccess'), 'success');
        setDeleteTarget(null);
        loadClients();
      }
    } catch (err) {
      console.error(err);
      const code = err.response?.data?.response?.code;
      showAlert(
        code === 'CLIENT_IN_USE' ? t('erp.clients.deleteInUse') : t('erp.clients.deleteError'),
        'error'
      );
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFiltered(clients);
      return;
    }

    const term = searchTerm.toLowerCase();

    setFiltered(
      clients.filter(
        (c) =>
          c.legalName?.toLowerCase().includes(term) ||
          c.rut?.toLowerCase().includes(term) ||
          c.industry?.toLowerCase().includes(term)
      )
    );
  }, [searchTerm, clients]);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>{t('erp.clients.title')}</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/erp/clients/new')}
          sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' }, textTransform: 'none' }}
        >
          {t('erp.clients.new')}
        </Button>
      </Box>

      <TextField
        fullWidth
        size="small"
        placeholder={t('erp.clients.searchPlaceholder')}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 2, maxWidth: 420 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
          ),
          endAdornment: searchTerm && (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setSearchTerm('')}>
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : filtered.length === 0 ? (
        <Alert severity="info">{t('erp.clients.empty')}</Alert>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E5E7EB' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('erp.clients.table.legalName')}</TableCell>
                <TableCell>{t('erp.clients.table.rut')}</TableCell>
                <TableCell>{t('erp.clients.table.industry')}</TableCell>
                <TableCell>{t('erp.clients.table.kam')}</TableCell>
                <TableCell>{t('erp.clients.table.frameworkContract')}</TableCell>
                <TableCell>{t('erp.clients.table.status')}</TableCell>
                <TableCell>{t('erp.clients.table.talana')}</TableCell>
                <TableCell align="right">{t('erp.common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((client) => (
                <TableRow
                  key={client.uid}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/erp/clients/${client.uid}`)}
                >
                  <TableCell sx={{ fontWeight: 600 }}>{client.legalName}</TableCell>
                  <TableCell>{formatRut(client.rut)}</TableCell>
                  <TableCell>{client.industry || '—'}</TableCell>
                  <TableCell>{client.kam?.name || '—'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={client.frameworkContract?.status === 'signed'
                        ? t('erp.clients.framework.signed')
                        : t('erp.clients.framework.pending')}
                      color={client.frameworkContract?.status === 'signed' ? 'success' : 'warning'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={client.status === 'active'
                        ? t('erp.clients.status.active')
                        : t('erp.clients.status.inactive')}
                      color={client.status === 'active' ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>{talanaChip(client.externalRefs?.talana)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title={t('erp.common.view')}>
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); navigate(`/erp/clients/${client.uid}`); }}
                      >
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('erp.clients.deleteClient')}>
                      <IconButton size="small" color="error"
                        onClick={(e) => { e.stopPropagation(); setDeleteTarget(client); }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Confirmación de eliminación (bloqueada si tiene cotizaciones) */}
      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('erp.clients.deleteClient')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
            {deleteTarget?.legalName || ''}
          </Typography>
          <Typography variant="body2">{t('erp.clients.deleteConfirmText')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} sx={{ textTransform: 'none' }}>
            {t('erp.common.cancel')}
          </Button>
          <Button variant="contained" color="error" disabled={deleting} onClick={handleDelete}
            sx={{ textTransform: 'none' }}>
            {t('erp.clients.deleteClient')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ErpClientsLayout;
